// Localised labels for DB-driven catalogue content (admin-entered translations, French fallback)
import React from 'react';
import { api } from './api';

// Shared technical glossary (attribute labels/values, product types) translated server-side once per language
const TERMS = {};
const PENDING = {};
export const loadTerms = (lang) => {
  if (!lang || lang === 'fr') return Promise.resolve({});
  if (TERMS[lang]) return Promise.resolve(TERMS[lang]);
  if (!PENDING[lang]) PENDING[lang] = api.get('/catalog/terms', { params: { lang } }).then(r => { TERMS[lang] = r.data?.terms || {}; return TERMS[lang]; }).catch(() => { delete PENDING[lang]; return {}; });
  return PENDING[lang];
};

export const useTerms = (lang) => {
  const [terms, setTerms] = React.useState(() => TERMS[lang] || {});
  React.useEffect(() => {
    let on = true;
    loadTerms(lang).then(t => { if (on) setTerms(t); });
    return () => { on = false; };
  }, [lang]);
  return React.useCallback((s) => (typeof s === 'string' && terms[s.replace(/\s+/g, ' ').trim()]) || s, [terms]);
};
export const catName = (category, lang) => (category && (category.name_i18n?.[lang] || category.name)) || '';

export const subName = (category, sub, lang) => category?.subcategory_i18n?.[lang]?.[sub] || sub;

export const localizeBanner = (banner, lang) => {
  const tr = banner?.i18n?.[lang];
  if (!tr) return banner;
  return { ...banner, title: tr.title || banner.title, subtitle: tr.subtitle || banner.subtitle, description: tr.description || banner.description, button_label: tr.button_label || banner.button_label };
};

export const prodName = (p, lang) => (p && (p.name_i18n?.[lang] || p.name)) || '';

export const prodDesc = (p, lang) => (p && (p.description_i18n?.[lang] || p.description)) || '';

// Category SEO block for the current language: FR = the native fields, other languages = admin/AI translation (or nothing)
export const catSeo = (c, lang) => {
  if (!c) return {};
  if (lang === 'fr') return c;
  const tr = c.seo_i18n?.[lang];
  return tr ? { ...tr, description: tr.description || c.description, faq: tr.faq?.length ? tr.faq : c.faq } : { description: c.description, faq: c.faq };
};

export const subSeoOf = (c, sub, lang) => (!c || !sub ? {} : lang === 'fr' ? c.subcategory_seo?.[sub] || {} : c.subcategory_seo_i18n?.[lang]?.[sub] || {});

export const pageTitle = (p, lang) => (p && (p.title_i18n?.[lang] || p.i18n?.[lang]?.title || p.title)) || '';

export const pageContent = (p, lang) => (p && (p.i18n?.[lang]?.content || p.content)) || '';

export const pageGroupLabel = (groups, groupsI18n, key, lang) => (lang !== 'fr' && groupsI18n?.[lang]?.[key]) || groups?.[key] || '';

export const localizeArticle = (a, lang) => {
  const tr = lang !== 'fr' && a?.i18n?.[lang];
  return tr ? { ...a, title: tr.title || a.title, description: tr.description || a.description, content: tr.content || a.content } : a;
};

// Product SEO block (h1, seo_title, meta_description, faq) in the UI language, FR fallback
export const prodSeo = (p, lang) => ({ ...(p?.seo || {}), ...((lang !== 'fr' && p?.seo_i18n?.[lang]) || {}) });
